import React from "react";
import { Button } from "@/components/ui/button";
import { reportClientError } from "@/lib/error-report";

type Props = {
  children: React.ReactNode;
};

type State = {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
};

// Shared between the boundary and the global rejection guard in providers.tsx so
// both paths burn the SAME one-shot reload. Two keys would mean two reloads.
const chunkReloadKey = "chunk-load-auto-reload-once";

/**
 * A stale-deploy chunk failure. Turbopack throws
 * `Error("Failed to load chunk <url> from module <id>")` and sets
 * `err.name = "ChunkLoadError"` afterwards, so the name — not the message — is the
 * reliable signal (webpack and Next set it too). The message patterns stay as a
 * fallback for webpack-era wording and for the MIME/HTML-error-page shapes a
 * missing chunk can produce. Takes `unknown` because rejection reasons aren't
 * guaranteed to be Errors.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false;
  if ((error as { name?: unknown }).name === "ChunkLoadError") return true;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\d]+ failed|ChunkLoadError|Failed to load module script|not a valid JavaScript MIME type|Unexpected token '<'|Failed to load chunk .*? (?:from module|from an HMR update|as a runtime dependency)/i.test(
    message,
  );
}

// `location.replace()` starts a navigation but does NOT stop this document from
// running. Until the new document commits, `location.search` is still the OLD
// url with no `__refresh` param, so componentDidUpdate's guard doesn't fire and
// any re-render in that window would re-arm the flag we just spent — buying a
// second unannounced hard reload that discards unsaved tool state. An in-memory
// latch closes that window; it dies with the document, which is exactly right.
let reloadInFlight = false;

/**
 * Re-arm the one-shot reload for a fresh page load. Swallows storage failures:
 * private modes can reject sessionStorage entirely, and this runs from
 * componentDidMount/DidUpdate where a throw would take the whole app down.
 */
function armChunkReload(): void {
  if (reloadInFlight) return;
  try {
    window.sessionStorage.setItem(chunkReloadKey, "0");
  } catch {
    // No storage means no one-shot guard, so tryChunkReloadOnce declines instead.
  }
}

/**
 * Reload once per session to pick up the new deploy's chunks. Returns false when
 * the session already used its reload so callers fall through to their normal
 * crash/report path instead of looping on a chunk that stays broken.
 */
export function tryChunkReloadOnce(): boolean {
  if (typeof window === "undefined") return false;
  if (reloadInFlight) return false;
  // sessionStorage throws outright in some privacy modes. This runs from an
  // unhandled-rejection handler as well as the boundary, so a throw here would
  // itself become an unhandled error — without the flag we can't guarantee
  // one-shot, so decline the reload rather than risk a loop.
  try {
    if (window.sessionStorage.getItem(chunkReloadKey) === "1") return false;
    window.sessionStorage.setItem(chunkReloadKey, "1");
  } catch {
    return false;
  }
  reloadInFlight = true;
  const url = new URL(window.location.href);
  url.searchParams.set("__refresh", String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  // Recover (rather than crash + report) a bounded number of times from benign
  // DOM-mutation errors caused by browser translation extensions.
  private benignRecoveryCount = 0;
  private static readonly maxBenignRecoveries = 3;

  // Browser translation extensions (Google Translate, Edge, etc.) swap React-owned
  // text nodes, so React's commit phase throws a NotFoundError whose message ends
  // in "... is not a child of this node". Match that specific phrase rather than a
  // bare method name so we don't mask genuine insertBefore/removeChild bugs in our
  // own code. This is transient extension noise, not our bug.
  private static isBenignDomMutationError(error: Error): boolean {
    return /is not a child of this node/i.test(error.message);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // If the session already spent its one reload, fall through to the crash
    // screen + report rather than reloading again.
    if (isChunkLoadError(error) && tryChunkReloadOnce()) {
      return;
    }

    // Translation-extension DOM mutations: don't report (it's not our bug and it
    // floods the feedback log), and try to recover by re-rendering a few times
    // before giving up and showing the crash screen.
    if (typeof window !== "undefined" && AppErrorBoundary.isBenignDomMutationError(error)) {
      if (this.benignRecoveryCount < AppErrorBoundary.maxBenignRecoveries) {
        this.benignRecoveryCount += 1;
        this.setState({ error: null, errorInfo: null });
        return;
      }
      this.setState({ errorInfo });
      return;
    }

    // Keep the real error in the console for debugging.
    console.error("App crashed:", error, errorInfo);
    reportClientError({ source: "render", message: error.message, stack: error.stack });
    this.setState({ errorInfo });
  }

  componentDidMount() {
    if (typeof window === "undefined") return;
    // Don't reset the flag if this mount is the result of a chunk-error
    // reload (indicated by the __refresh param tryChunkReloadOnce sets).
    // Resetting it here would create an infinite reload loop if the chunk
    // keeps failing after the forced refresh.
    const isPostReload = new URLSearchParams(window.location.search).has("__refresh");
    if (!isPostReload) {
      armChunkReload();
    }
  }

  componentDidUpdate() {
    if (typeof window === "undefined") return;
    // Same reason as componentDidMount: a page that already is the post-reload
    // attempt must keep the flag set. Chunk failures raised from event handlers
    // (see providers.tsx) arrive long after mount, when this render-is-fine reset
    // would otherwise have re-armed the reload on every interaction.
    if (new URLSearchParams(window.location.search).has("__refresh")) return;
    if (!this.state.error) {
      armChunkReload();
    }
  }

  render() {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="glass-panel rounded-2xl p-8 max-w-2xl w-full space-y-4">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              The page crashed while rendering. The error details are below.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <div className="text-sm font-medium">{error.name}: {error.message}</div>
            {error.stack ? (
              <pre className="mt-3 text-[11px] leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {error.stack}
              </pre>
            ) : null}
            {errorInfo?.componentStack ? (
              <pre className="mt-3 text-[11px] leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {errorInfo.componentStack}
              </pre>
            ) : null}
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => this.setState({ error: null, errorInfo: null })}
            >
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </div>
      </div>
    );
  }
}
